'use client';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement } from 'react';

export type InfoFootnoteProps = {
  /** The note, shown as text: markup characters are not read as markup. Copy comes from the caller. */
  children: string;
  'data-testid'?: string;
};

/**
 * A footnote at the end of a screen: small secondary text with an info icon, under a hairline. The
 * icon is decoration, so a screen reader reads the text alone. It sits beside the request screens
 * rather than in `ClarityNotice`, which is a notice box, not footer text (618 J6).
 */
function InfoFootnoteComponent({
  children,
  'data-testid': dataTestId,
}: Readonly<InfoFootnoteProps>): ReactElement {
  return (
    <Box
      role="note"
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.semantic.spacing.xs,
        paddingTop: theme.semantic.spacing.sm,
        borderTop: `${theme.semantic.spacing['4xs']} solid ${theme.palette.divider}`,
      })}
    >
      <InfoOutlinedIcon
        aria-hidden
        sx={(theme: Theme) => ({
          flex: 'none',
          // Centres the icon on the first line: the line box is 21px and the icon 16px, so the
          // icon sits 2.5px down. 2px is the nearest spacing token.
          marginTop: theme.semantic.spacing['3xs'],
          fontSize: theme.semantic.fontSize.base,
          color: theme.palette.text.tertiary,
        })}
      />
      <Typography
        variant="body2"
        component="p"
        sx={(theme: Theme) => ({
          margin: 0,
          minWidth: 0,
          // A comfortable line length on a wide page. The board draws 720px; the nearest size is 600px.
          maxWidth: theme.semantic.size.sm,
          lineHeight: theme.semantic.lineHeight.long,
          color: theme.palette.text.tertiary,
        })}
      >
        {children}
      </Typography>
    </Box>
  );
}

export const InfoFootnote = withClarityTheme(InfoFootnoteComponent);
